import { isAction, type Middleware } from "@reduxjs/toolkit"

import { perfOptIn } from "@/lib/perf/perf-setup"

// Every Redux dispatch's cost, for the slow-things report
// (components/perf-report). The outer middleware sits first in the chain and
// the inner one last, right before the store's own dispatch, so their
// difference is what the middleware in between cost. Without the opt-in both
// hand back `next` itself, so a normal session's dispatches skip them.
//
// The store is built before the session is known, so the opt-in alone arms
// this. The launcher drops the records if the user turns out not to be a
// Mindtrip user; they never leave the tab before that.

type ActionRecord = {
  type: string
  start: number
  // the dispatch minus any dispatches nested in it: middleware, reducers and
  // the store subscribers (useSelector checks) that ran synchronously
  selfMs: number
  // reducers plus the store subscribers notified synchronously
  storeMs: number
  // the outermost dispatch this one ran inside: middleware dispatching more
  // actions nests them, sometimes many levels deep
  root?: string
}

const MAX_RECORDS = 5000
const records: ActionRecord[] = []
const storeMsByAction = new WeakMap<object, number>()

export function getActionRecords() {
  return records
}

export function clearActionRecords() {
  records.length = 0
}

// RTK Query's actions all share a handful of types; the endpoint is what
// tells them apart. A rejection whose `meta.condition` is set is a thunk that
// never ran (the query was already in flight or cached), not a failed request,
// so it is labelled as skipped rather than counted among the rejections.
export function labelOf(action: { type: string; meta?: unknown }) {
  const { meta } = action
  const details: string[] = []
  if (meta && typeof meta === "object") {
    if ("arg" in meta) {
      const { arg } = meta
      if (arg && typeof arg === "object" && "endpointName" in arg)
        details.push(String(arg.endpointName))
    }
    if ("condition" in meta && meta.condition === true) details.push("skipped")
  }
  return details.length ? `${action.type} (${details.join(", ")})` : action.type
}

export function perfActionsMiddleware(): Middleware {
  if (!perfOptIn()) return () => (next) => next
  // per level of nesting: the dispatch's label and the time spent in
  // dispatches nested inside it
  const stack: { type: string; nested: number }[] = []
  return () => (next) => (action) => {
    if (!isAction(action)) return next(action)
    const start = performance.now()
    const type = labelOf(action)
    const parent = stack.at(-1)
    const root = stack[0]?.type
    stack.push({ type, nested: 0 })
    try {
      return next(action)
    } finally {
      const total = performance.now() - start
      const inner = stack.pop()?.nested ?? 0
      if (parent) parent.nested += total
      records.push({
        type,
        start,
        selfMs: total - inner,
        storeMs: storeMsByAction.get(action) ?? 0,
        root,
      })
      if (records.length > MAX_RECORDS)
        records.splice(0, records.length - MAX_RECORDS)
    }
  }
}

export function perfStoreTimingMiddleware(): Middleware {
  if (!perfOptIn()) return () => (next) => next
  return () => (next) => (action) => {
    if (!isAction(action)) return next(action)
    const start = performance.now()
    try {
      return next(action)
    } finally {
      storeMsByAction.set(action, performance.now() - start)
    }
  }
}
